/*
 * Drawer -- the modal surface primitive: opening, closing, modality and focus
 * for every drawer. Published on `window.Faubourg.Drawer`, never registered:
 * a base class that registers itself cannot be extended twice.
 *
 * Kept short, under the per-page script budget; its contract is pinned by
 * bin/test-drawer.mjs.
 */

(function () {
  'use strict';

  var scope = typeof window !== 'undefined' ? window : globalThis;

  // `summary` is a real tab stop; `tabindex="-1"` is filtered out below.
  var FOCUSABLE = 'a[href], button, input, select, textarea, summary, [tabindex]';

  // The exit: non-interactive and out of the a11y tree, but still painted.
  var CLOSING = 'data-drawer-closing';

  // Shared by every instance: the whole exclusivity mechanism.
  var active = null;

  function isRendered(element) {
    if (!element || !element.isConnected) return false;
    if (element.closest('[hidden]')) return false;
    if (element.closest('[inert]')) return false;

    // A folded-away disclosure still contains its controls; only its summary
    // is reachable, which no selector can express.
    var node = element;
    while (node && node.parentNode && node.parentNode.tagName) {
      var parent = node.parentNode;
      if (parent.tagName === 'DETAILS' && !parent.hasAttribute('open')) {
        if (node.tagName !== 'SUMMARY') return false;
      }
      node = parent;
    }

    // Layout is the last word, and the one thing a test stub cannot infer.
    if (typeof element.getClientRects === 'function') {
      if (element.getClientRects().length === 0) return false;
    }

    return true;
  }

  function isFocusable(element) {
    if (!element || typeof element.matches !== 'function') return false;
    if (!element.matches(FOCUSABLE)) return false;
    if (element.disabled === true || element.hasAttribute('disabled')) return false;
    if (element.getAttribute('tabindex') === '-1') return false;
    return isRendered(element);
  }

  class Drawer extends HTMLElement {
    #controller = null;
    #trigger = null;
    #inerted = [];
    #exit = null;
    #raised = false;

    // Lets a specialisation hand the focus to a control of its own without
    // reimplementing "is this reachable". False when it found nothing.
    static focusFirst(root) {
      if (!root || typeof root.querySelectorAll !== 'function') return false;

      var stops = Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), isFocusable);
      if (stops.length === 0) return false;

      stops[0].focus();
      return true;
    }

    connectedCallback() {
      // Delegated from `document`, so a trigger rendered later works; one
      // controller per connection, so the editor's reconnections never stack.
      this.#controller = new AbortController();
      var signal = this.#controller.signal;

      document.addEventListener('click', this.#onClick, { signal: signal });
      document.addEventListener('keydown', this.#onKeydown, { signal: signal });

      // The editor redraws a section on its own: a header drawn after this
      // drawer connected brings triggers still in their no-script state.
      document.addEventListener('shopify:section:load', this.#upgrade, { signal: signal });

      this.#upgrade();
    }

    disconnectedCallback() {
      if (this.#controller) {
        this.#controller.abort();
        this.#controller = null;
      }

      // A removed element cannot be closed by the user, so a veto here would
      // strand the page: inert, unscrollable, with no way back.
      this.close({ force: true, returnFocus: false, reason: 'disconnected' });
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    // Modeless: the page stays usable, unlocked and not inert, and the drawer
    // holds keys only while the focus is inside it.
    get modeless() {
      return this.hasAttribute('data-drawer-modeless');
    }

    // What opened this drawer, while it is open. A surface that replaces this
    // one reads it to hand the focus back past it, to where the shopper was.
    get opener() {
      return this.#trigger;
    }

    open(options) {
      var settings = options || {};
      if (this.isOpen) return false;

      // Instant, not forced: the outgoing drawer's veto still decides.
      if (active && active !== this) {
        var replaced = active.close({
          returnFocus: false,
          animate: false,
          reason: 'drawer-replaced',
        });
        if (!replaced) return false;
      }

      var trigger = settings.trigger || null;
      var reason = settings.reason || null;

      if (!this.#emit('drawer:before-open', trigger, reason, true)) return false;

      // A reopening cancels any exit rather than animating against it.
      this.#endExit();
      this.#raise();

      this.#trigger = trigger;
      this.setAttribute('open', '');
      this.removeAttribute('inert');
      this.setAttribute('aria-hidden', 'false');
      if (!this.modeless) {
        this.#inertPage();
        document.documentElement.setAttribute('data-drawer-open', this.id);
      }
      active = this;
      this.#syncTriggers();
      if (settings.focus !== false) this.#focusPanel();
      this.#emit('drawer:open', trigger, reason, false);

      return true;
    }

    close(options) {
      var settings = options || {};
      if (!this.isOpen) return false;

      var trigger = this.#trigger;
      var reason = settings.reason || null;
      var force = settings.force === true;

      if (!force && !this.#emit('drawer:before-close', trigger, reason, true)) return false;

      // Modality goes first, without waiting for the exit.
      this.setAttribute(CLOSING, '');
      this.removeAttribute('open');
      this.setAttribute('inert', '');
      this.setAttribute('aria-hidden', 'true');
      this.#restorePage();

      if (document.documentElement.getAttribute('data-drawer-open') === this.id) {
        document.documentElement.removeAttribute('data-drawer-open');
      }

      if (active === this) active = null;
      this.#trigger = null;
      this.#syncTriggers();
      this.#startExit(force || settings.animate === false);

      // Only to a control still able to receive it: a trigger removed by a
      // re-render would drop the focus on `body` and lose the reader's place.
      if (settings.returnFocus !== false && isFocusable(trigger)) trigger.focus();

      this.#emit('drawer:close', trigger, reason, false);

      return true;
    }

    toggle(options) {
      return this.isOpen ? this.close(options) : this.open(options);
    }


    // CSS decides whether the exit animates (`none` under reduced motion).
    #startExit(immediate) {
      this.#abortExit();

      var parts = immediate ? [] : this.#exitAnimated();
      if (parts.length === 0) {
        this.removeAttribute(CLOSING);
        this.#lower();
        return;
      }

      var controller = new AbortController();
      var pending = parts.length;
      var drawer = this;

      this.#exit = controller;

      parts.forEach(function (part) {
        var counted = false;

        // A drawer hidden mid-exit fires only `animationcancel`.
        var settle = function () {
          if (counted) return;
          counted = true;

          pending -= 1;
          if (pending > 0) return;

          controller.abort();
          if (drawer.#exit === controller) drawer.#exit = null;
          drawer.removeAttribute(CLOSING);
          drawer.#lower();
        };

        part.addEventListener('animationend', settle, { signal: controller.signal });
        part.addEventListener('animationcancel', settle, { signal: controller.signal });
      });
    }

    #exitAnimated() {
      if (typeof scope.getComputedStyle !== 'function') return [];

      return [
        this.querySelector('[data-drawer-panel]'),
        this.querySelector('[data-drawer-overlay]'),
      ].filter(function (part) {
        var name = part && scope.getComputedStyle(part).animationName;
        return name && name !== 'none';
      });
    }

    #abortExit() {
      this.#exit?.abort();
      this.#exit = null;
    }

    #endExit() {
      this.#abortExit();
      this.removeAttribute(CLOSING);
    }

    // With `popover` the drawer opens in the top layer, above any stacking
    // context holding it, and leaves once its exit has played.
    #raise() {
      if (this.#raised || !this.hasAttribute('popover') || !this.showPopover) return;
      this.showPopover();
      this.#raised = true;
    }

    #lower() {
      if (this.#raised && this.isConnected) this.hidePopover();
      this.#raised = false;
    }


    #onClick = (event) => {
      var target = event.target;
      if (!target || typeof target.closest !== 'function') return;

      var trigger = target.closest('[data-drawer-target]');
      if (trigger) {
        // An id, never a selector: a trigger must not aim an arbitrary query
        // at the page. An id naming something else is simply not a trigger.
        var wanted = document.getElementById(trigger.dataset.drawerTarget);
        if (wanted instanceof Drawer && wanted === this) {
          this.toggle({ trigger: trigger, reason: 'trigger' });
        }
        return;
      }

      if (!this.isOpen || !this.contains(target)) return;

      if (target.closest('[data-drawer-close]')) {
        this.close({ reason: 'close-button' });
        return;
      }

      // The overlay is the only part of the subtree that dismisses the drawer.
      if (target.closest('[data-drawer-overlay]')) this.close({ reason: 'overlay' });
    };

    #onKeydown = (event) => {
      if (!this.isOpen || active !== this) return;
      if (this.modeless && !this.contains(document.activeElement)) return;

      if (event.key === 'Escape') {
        // Cancelling the key claims something happened. If the close was
        // vetoed, nothing did, and Escape keeps its browser meaning.
        if (this.close({ reason: 'escape' })) event.preventDefault();
        return;
      }

      if (event.key === 'Tab' && !this.modeless) this.#trapTab(event);
    };

    #trapTab(event) {
      var panel = this.#panel();
      var stops = this.#focusStops(panel);

      if (stops.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }

      var first = stops[0];
      var last = stops[stops.length - 1];
      var current = document.activeElement;

      // Focus that drifted out comes back at the edge they are heading for.
      if (!panel.contains(current)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && current === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && current === last) {
        event.preventDefault();
        first.focus();
      }
    }


    #panel() {
      var panel = this.querySelector('[data-drawer-panel]') || this;
      if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      return panel;
    }

    #focusStops(panel) {
      return Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), isFocusable);
    }

    #focusPanel() {
      var panel = this.#panel();
      var declared = Array.prototype.slice
        .call(this.querySelectorAll('[data-drawer-initial-focus]'))
        .filter(isFocusable)[0];

      if (declared) {
        declared.focus();
        return;
      }

      var stops = this.#focusStops(panel);
      if (stops.length > 0) {
        stops[0].focus();
        return;
      }

      panel.focus();
    }

    #inertPage() {
      // Everything off the path from the drawer up to `body` goes inert: out
      // of the tab order and the a11y tree at once. Only what this drawer
      // marked is recorded, so someone else's `inert` survives the close.
      this.#inerted = [];

      var node = this;
      while (node && node !== document.body && node.parentNode && node.parentNode.tagName) {
        var siblings = node.parentNode.children;
        for (var index = 0; index < siblings.length; index += 1) {
          var sibling = siblings[index];
          if (sibling === node) continue;
          if (sibling.hasAttribute('inert')) continue;
          sibling.setAttribute('inert', '');
          this.#inerted.push(sibling);
        }
        node = node.parentNode;
      }
    }

    #restorePage() {
      for (var index = 0; index < this.#inerted.length; index += 1) {
        this.#inerted[index].removeAttribute('inert');
      }
      this.#inerted = [];
    }


    #triggers() {
      if (!this.id) return [];
      return document.querySelectorAll('[data-drawer-target="' + this.id + '"]');
    }

    #syncTriggers() {
      var expanded = this.isOpen ? 'true' : 'false';
      var triggers = this.#triggers();
      for (var index = 0; index < triggers.length; index += 1) {
        triggers[index].setAttribute('aria-expanded', expanded);
      }
    }

    #upgrade = () => {
      this.#upgradeMarkup();
      this.#syncTriggers();
    };

    #upgradeMarkup() {
      // Script-only controls are revealed for this drawer's id alone: a
      // blanket unhide would resurrect every unrelated hidden node.
      if (!this.id) return;

      var triggers = this.#triggers();
      for (var index = 0; index < triggers.length; index += 1) {
        triggers[index].removeAttribute('hidden');
      }

      var fallbacks = document.querySelectorAll('[data-drawer-fallback="' + this.id + '"]');
      for (var fallback = 0; fallback < fallbacks.length; fallback += 1) {
        fallbacks[fallback].setAttribute('hidden', '');
      }
    }


    #emit(type, trigger, reason, cancelable) {
      // Business rules listen for these rather than reading `open` or writing
      // `inert`; the two `before-` events are their veto.
      return this.dispatchEvent(
        new CustomEvent(type, {
          bubbles: true,
          composed: true,
          cancelable: cancelable,
          detail: {
            drawer: this,
            id: this.id,
            trigger: trigger || null,
            reason: reason || null,
          },
        })
      );
    }
  }

  scope.Faubourg = scope.Faubourg || {};
  scope.Faubourg.Drawer = Drawer;
})();
